import { useEffect, useRef, useState } from 'react';
import { Map as LeafletMap, TileLayer, Marker, Popup } from 'leaflet';
import { Button } from '@/components/ui/button';
import { Navigation, MapPin, Store } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import { supabase } from '@/integrations/supabase/client';
import { geocodeAddress } from '@/utils/osmGeocoding';

// Fix for default markers
import L from 'leaflet';
import { addBasemap, MAP_TILE_URL, MAP_TILE_OPTIONS } from '@/config/mapTiles';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface Order {
  id: string;
  delivery_address: string;
  customer_name: string;
  status: string; // Changed from specific union type to string to match DriverDashboardPage
  restaurants: {
    name: string;
    address: string;
  };
}

interface DriverMapProps {
  currentOrder?: Order | null;
  queuedOrders?: Order[];
  driverLocation?: { lat: number; lng: number } | null;
  onNavigate?: (destination: 'pickup' | 'dropoff') => void;
}

const DriverMap = ({ currentOrder, queuedOrders = [], driverLocation, onNavigate }: DriverMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<LeafletMap | null>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(driverLocation || null);

  // Fetch customer live location for an order
  const fetchCustomerLiveLocation = async (orderId: string) => {
    try {
      console.log('Fetching live location for order:', orderId);
      const { data, error } = await supabase
        .from('user_locations')
        .select('latitude, longitude, accuracy, created_at')
        .eq('order_id', orderId)
        .eq('is_live', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Database error fetching live location:', error);
        return null;
      }

      if (!data) {
        console.log('No live location found in database for order:', orderId);
        return null;
      }

      console.log('Live location found:', data);
      return {
        latitude: Number(data.latitude),
        longitude: Number(data.longitude),
        accuracy: Number(data.accuracy || 0)
      };
    } catch (error) {
      console.error('Error fetching customer live location:', error);
      return null;
    }
  };

  // Default to Banjul, Gambia if no location
  const defaultLocation = { lat: 13.4549, lng: -16.5790 };
  const currentLocation = userLocation || driverLocation || defaultLocation;

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Create map with black and white theme
    const map = new LeafletMap(mapRef.current, {
      center: [currentLocation.lat, currentLocation.lng],
      zoom: 13,
      zoomControl: true,
    });

    // Add light tile layer with black roads (CartoDB Positron)
    addBasemap(map);

    mapInstanceRef.current = map;

    // Get current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          map.setView([location.lat, location.lng], 15);
        },
        (error) => {
          console.log('Location access denied or failed:', error);
        }
      );
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update map center when driver location changes (only if significantly different)
  useEffect(() => {
    if (mapInstanceRef.current && driverLocation) {
      const currentLoc = userLocation || defaultLocation;
      const distance = Math.sqrt(
        Math.pow(driverLocation.lat - currentLoc.lat, 2) + 
        Math.pow(driverLocation.lng - currentLoc.lng, 2)
      );
      
      // Only update if moved more than ~100 meters (0.001 degrees)
      if (distance > 0.001) {
        mapInstanceRef.current.setView([driverLocation.lat, driverLocation.lng], mapInstanceRef.current.getZoom());
        setUserLocation(driverLocation);
      }
    }
  }, [driverLocation, userLocation]);

  // Add markers for current location, orders, etc.
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    
    // Clear existing markers
    map.eachLayer((layer) => {
      if (layer instanceof Marker) {
        map.removeLayer(layer);
      }
    });

    // Add driver location marker (blue)
    if (currentLocation) {
      const driverIcon = L.divIcon({
        html: '<div style="background-color: #3b82f6; width: 20px; height: 20px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>',
        iconSize: [26, 26],
        className: 'custom-div-icon'
      });

      new Marker([currentLocation.lat, currentLocation.lng], { icon: driverIcon })
        .addTo(map)
        .bindPopup('<strong>Your Location</strong><br/>Driver');
    }

    // Add current order markers
    if (currentOrder) {
      // Restaurant marker (green for pickup, amber for ready/on-way)
      const restaurantIcon = L.divIcon({
        html: `<div style="background-color: ${
          currentOrder.status === 'picked_up' ? '#10b981' : 
          currentOrder.status === 'ready' ? '#3b82f6' : '#f59e0b'
        }; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        iconSize: [20, 20],
        className: 'custom-div-icon'
      });

      // Use real restaurant location by geocoding restaurant address
      const restaurantAddress = currentOrder.restaurants?.address;
      const restaurantName = currentOrder.restaurants?.name || 'Restaurant';
      (restaurantAddress ? geocodeAddress(restaurantAddress) : Promise.resolve(null)).then(restaurantCoords => {
        let restaurantLat, restaurantLng;

        if (restaurantCoords) {
          restaurantLat = restaurantCoords.latitude;
          restaurantLng = restaurantCoords.longitude;
        } else if (currentLocation) {
          // Fallback to current location if geocoding fails
          restaurantLat = currentLocation.lat + (Math.random() - 0.5) * 0.01;
          restaurantLng = currentLocation.lng + (Math.random() - 0.5) * 0.01;
        } else {
          return;
        }

        new Marker([restaurantLat, restaurantLng], { icon: restaurantIcon })
          .addTo(map)
          .bindPopup(`<strong>${restaurantName}</strong><br/>Pickup Location<br/>${restaurantAddress || ''}`);
      });


      // Customer marker (red for delivery) - try to use live location
      const customerIcon = L.divIcon({
        html: '<div style="background-color: #ef4444; width: 16px; height: 16px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [20, 20],
        className: 'custom-div-icon'
      });

      // Check for live customer location and add marker
      const handleCustomerLocation = async () => {
        const liveLocation = await fetchCustomerLiveLocation(currentOrder.id);
        let customerLat, customerLng;
        let popupText = `<strong>${currentOrder.customer_name}</strong><br/>Delivery Location<br/>${currentOrder.delivery_address}`;
        
        if (liveLocation) {
          // Use actual live customer location
          console.log('Using live customer location:', liveLocation);
          customerLat = liveLocation.latitude;
          customerLng = liveLocation.longitude;
          popupText += '<br/><em style="color: #10b981;">📍 Live Location</em>';
          
          // Add customer marker with live location
          new Marker([customerLat, customerLng], { icon: customerIcon })
            .addTo(map)
            .bindPopup(popupText);
        } else {
          // Try to geocode the delivery address as fallback
          console.log('No live location, trying to geocode address:', currentOrder.delivery_address);
          try {
            const addressCoords = await geocodeAddress(currentOrder.delivery_address);
            if (addressCoords) {
              console.log('Successfully geocoded address:', addressCoords);
              customerLat = addressCoords.latitude;
              customerLng = addressCoords.longitude;
              popupText += '<br/><em style="color: #f59e0b;">📍 Address Location</em>';
              
              // Add customer marker with geocoded location
              new Marker([customerLat, customerLng], { icon: customerIcon })
                .addTo(map)
                .bindPopup(popupText);
            } else {
              console.warn('Could not geocode address, no customer marker will be shown');
              // Don't add a marker if we can't determine location
            }
          } catch (geocodeError) {
            console.error('Geocoding failed:', geocodeError);
            // Don't add a marker if geocoding fails
          }
        }
      };
      
      // Call the async function
      handleCustomerLocation();
    }

    // Add queued orders (gray markers) - use real restaurant locations
    queuedOrders.forEach((order, index) => {
      const queuedIcon = L.divIcon({
        html: '<div style="background-color: #6b7280; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>',
        iconSize: [16, 16],
        className: 'custom-div-icon'
      });

      // Use real restaurant locations for queued orders
      geocodeAddress(order.restaurants?.address || order.delivery_address).then(coords => {
        let orderLat, orderLng;
        
        if (coords) {
          orderLat = coords.latitude;
          orderLng = coords.longitude;
        } else if (currentLocation) {
          // Fallback to approximate location
          orderLat = currentLocation.lat + (Math.random() - 0.5) * 0.03;
          orderLng = currentLocation.lng + (Math.random() - 0.5) * 0.03;
        } else {
          return;
        }

        new Marker([orderLat, orderLng], { icon: queuedIcon })
          .addTo(map)
          .bindPopup(`<strong>Queued Order ${index + 1}</strong><br/>${order.restaurants?.name || 'Restaurant'}<br/>Customer: ${order.customer_name}`);
      });

    });

  }, [currentOrder, queuedOrders, currentLocation]);

  const centerOnDriver = () => {
    if (mapInstanceRef.current && currentLocation) {
      mapInstanceRef.current.setView([currentLocation.lat, currentLocation.lng], 16);
    }
  };

  const handleNavigateToPickup = () => {
    if (onNavigate && currentOrder) {
      onNavigate('pickup');
    }
  };

  const handleNavigateToDropoff = () => {
    if (onNavigate && currentOrder) {
      onNavigate('dropoff');
    }
  };

  return (
    <div className="relative isolate w-full h-full bg-background overflow-hidden">
      <div ref={mapRef} className="relative z-0 w-full h-full" />
      
      {/* Map Controls */}
      <div className="absolute z-[1000] top-16 right-4 flex flex-col gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={centerOnDriver}
          className="bg-background/80 backdrop-blur-sm hover:bg-background/90"
        >
          <Navigation className="w-4 h-4 mr-1" />
          Center
        </Button>
        
        {currentOrder && (
          <div className="flex flex-col gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={handleNavigateToPickup}
              className="bg-amber-500/80 text-white border-amber-600 hover:bg-amber-600/90 backdrop-blur-sm"
            >
              <Store className="w-4 h-4 mr-1" />
              To Pickup
            </Button>
            
            {(currentOrder && (currentOrder.status === 'picked_up' || currentOrder.status === 'ready')) && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleNavigateToDropoff}
                className="bg-red-500/80 text-white border-red-600 hover:bg-red-600/90 backdrop-blur-sm"
              >
                <MapPin className="w-4 h-4 mr-1" />
                To Customer
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="absolute z-[1000] bottom-4 left-4 bg-background/80 backdrop-blur-sm rounded-lg p-3 text-sm">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded-full border-2 border-white"></div>
            <span>You</span>
          </div>
            {currentOrder && (
              <>
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 ${
                    currentOrder.status === 'picked_up' ? 'bg-green-500' : 
                    currentOrder.status === 'ready' ? 'bg-blue-500' : 'bg-amber-500'
                  } rounded-full border border-white`}></div>
                  <span>Pickup</span>
                </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                <span>Delivery</span>
              </div>
            </>
          )}
          {queuedOrders.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-gray-500 rounded-full border border-white"></div>
              <span>Queued ({queuedOrders.length})</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverMap;